/**
 * The words the game's page adds to the editor: the login at its door, saving and its conflicts, publishing, the
 * status cells, live editing with other builders, an editor update, a sign-in that ended, and the key commands'
 * names and refusals. Each is a draft for the author unless the code that asks for it says otherwise. Sentence
 * case; a sentence shown alone ends with a full stop, and a title, a button, a status cell or a notice line that
 * the log shows ends with none.
 *
 * A value that chooses its form by `count` prints it only where it holds `{count}`. `{who}` is a builder's family
 * name, or `builder.another`; `builder.anotherInside` is the same as it reads inside a sentence. `{edits}` is
 * `stream.edits`. `{names}` is a list of names joined by a comma and a space, cut short with `signIn.listMore`.
 * `{time}` is a time of day and `{when}` a day and a time, on the reader's own clock. `{reason}` is what a server or
 * the browser said, shown as it came. `{direction}` is one of `keys.directions`. `{head}` in `reopen.*` is one of
 * its three openings, and `{losing}` and `{kept}` are `signIn.losing.*` and `signIn.kept.*`. A value that ends
 * without a full stop where a sentence continues (`behind.cause.*`) has its stop added by the page, which adds
 * none to a reason that already ends with one. In `brush.why.none` and `brush.why.few`, `{reason}` is one of
 * `brush.why.gap`, `props`, `ground`, `edge` or `full`, and `{count}` of the first four is the tries a rule refused
 * and `{tried}` the tries made; in `brush.stroke.*`, `{count}` is the props placed and `{cleared}` the ground cover
 * removed under them, except in `stoppedCleared` and `alsoCleared`, where `{count}` is the ground cover removed; in
 * `brush.deletedBy`, `{name}` is the brush set and `{who}` the builder who deleted it. In
 * `spawn.*`, `{cap}` is the highest level a creature may have. `gate.*` words a gate's Lock, Unlock and Closes by itself fields.
 */
export const EDITOR = {
  builder: {
    another: "Another builder",
    anotherInside: "another builder",
  },
  timing: {
    builderReadOnly: "Saving a timing needs a role that may change footprints",
    studioReadOnly: "Saving a strip's timing in the studio needs the Designer's role",
  },
  footprint: {
    olderShown: "The footprint shown is older than the one saved; reload it to see the change",
    bandUnread: "The footprint the art gives at this frame's band could not be read, so the saved footprint is not shown; try Reload again",
  },
  failure: {
    unreached: "The editor could not reach the server. Nothing was lost; check your connection and try again.",
  },
  door: {
    title: "Log in",
    intro: "Log in with your OdysseyTales game account.",
    email: "Email",
    password: "Password",
    busy: "Logging in",
    empty: "Enter your email and password.",
    naming: "Finish creating your character in the game first.",
    ended: "Your login ended. Log in again.",
    unavailable: "The build server does not answer. Try again in a moment; if it keeps failing, reload the page.",
    rate: "Too many checks for this login. Try again in a minute.",
    rateSeconds: { one: "Too many checks for this login. Try again in {count} second.", other: "Too many checks for this login. Try again in {count} seconds." },
    rateMinutes: { one: "Too many checks for this login. Try again in {count} minute.", other: "Too many checks for this login. Try again in {count} minutes." },
    refusedTitle: "This account cannot build",
    refused: "{name} holds no Builder or Designer role. Ask a lead to grant one, then log in again.",
    unavailableTitle: "The build server does not answer",
    anotherAccount: "Log in with another account",
    logOut: "Log out",
    retry: "Try again",
    thisAccount: "This account",
  },
  reopen: {
    account: "You are now logged in as {next}, and this tab was opened as {first}.",
    rights: "What {first} may do in the editor changed while you were logged out.",
    mode: "The server changed how it takes edits while you were logged out.",
    reload: "{head} Reload the page to carry on; {kept}.",
    download: "{head} The page was not reloaded. Do not reload yet: this tab would lose {losing}. Download your version first.",
    downloadNoFootprints: "{head} The page was not reloaded. Do not reload yet: this tab would lose {losing}. Download your version first. The download does not hold the footprints.",
    none: "{head} The page was not reloaded. Do not reload the page: this tab would lose its unsaved footprints. Save them if this account still may, then reload.",
    noneKept: "{head} The page was not reloaded. Do not reload the page: this tab would lose its unsaved footprints, which are kept for {first} alone. Save them if this account still may, then reload.",
  },
  save: {
    title: "Someone saved this world",
    merge: "Bring in their changes and keep mine",
    merging: "Bringing their changes in",
    copyLabel: "Save the copy as (world id)",
    keep: "Keep editing",
    saveCopy: "Save as a copy",
    saving: "Saving the copy",
    download: "Download my version",
    downloading: "Preparing the file",
    badId: "A world id is 1 to 64 lowercase letters, digits and -.",
    taken: "A world {id} exists. Choose another id.",
    declined: "Nothing was changed: the choice between your changes and theirs was left for later. Your changes are still in this tab.",
    notSaved: "Their changes were brought in, but the save did not land: {reason} Your changes are still in this tab.",
    downloaded: "Downloaded {name}. It holds this tab's version; your changes are still in this tab too.",
    downloadFailed: "The file could not be made: {reason}",
    nothing: "Nothing to save",
    footprintsSaved: { one: "Footprint saved; the world had no changes", other: "{count} footprints saved; the world had no changes" },
    otherTab: "This world was saved from another tab. Your changes are only in this tab: bring the saved version in, or save a copy, before you leave it.",
    deleted: "{who} deleted this world at {time}. Your changes are only in this tab: save them as a copy or download them before you leave it.",
    savedBy: "{who} saved at {time}. Your changes are only in this tab: bring theirs in, or save a copy, before you leave it.",
    copyFailed: "The copy was not saved ({status}): {reason}",
  },
  publish: {
    published: "Published {version}",
    recompiling: "Publish refused: a recompile is running, and the editor reloads when it is done",
    rollingBack: "Publish refused: a rollback is running; publish once it is done",
  },
  publishing: {
    phase: { compile: "preparing", assets: "uploading pictures", world: "uploading the map", pointer: "going live" },
    line: "{who} is publishing: {phase}",
    withLeft: "{what}, {left}",
    left: { seconds: "about {seconds} s left", minutes: "about {minutes} min left", almost: "almost done", late: "longer than usual" },
    started: "{who} started publishing. Changes made from now on are not in that version.",
    published: "{who} published {version}. Changes made since are not in it.",
    noVersion: "a new version",
    failed: "{who}'s publish did not go through: {reason}.",
    reason: {
      unsaved: "the live world could not be saved first",
      missing: "the world has nothing saved to publish",
      compile: "the world did not compile",
      checks: "the world did not pass the publish checks",
      sheets: "character sheets are missing on the game's storage",
      upload: "the upload to the game's storage failed",
      unrecorded: "it is live, but its record was not written",
      other: "something went wrong on the server",
    },
    lost: "A publish by {who} ended while this tab was offline. Versions shows whether it went live.",
    stale: "No word about the publish for 45 seconds, so the notice was cleared.",
    dialog: "{who} is publishing this world now. A second publish is refused until theirs ends.",
  },
  status: {
    activities: { saving: "Saving", compiling: "Compiling", publishing: "Publishing", rollingBack: "Rolling back" },
    justNow: "just now",
    secondsAgo: "{seconds}s ago",
    minutesAgo: "{minutes}m ago",
    hoursAgo: "{hours}h ago",
    saveFailed: "Save failed",
    saved: "Saved {ago}",
    publishedVersion: "Published {version}",
    publishedAgo: "Published {ago}",
    publishedVersionAgo: "Published {version} {ago}",
    publishFailed: "Publish failed",
    publishRefused: "Publish refused",
  },
  live: {
    title: "Both of you changed this",
    choice: "Keep",
    mine: "Keep mine",
    theirs: "Take theirs",
    allMine: "Keep all mine",
    allTheirs: "Take all theirs",
    goTo: "Go to",
    later: "Not now",
    saveCopy: "Save as a copy",
    apply: "Apply",
    editors: "Builders here",
    replacingPending: {
      one: "{who} is replacing this world. {count} edit of yours had no answer yet and will not be kept. This tab reconnects in a moment and shows the new version.",
      other: "{who} is replacing this world. {count} edits of yours had no answer yet and will not be kept. This tab reconnects in a moment and shows the new version.",
    },
    replacing: "{who} is replacing this world. This tab reconnects in a moment and shows the new version.",
    full: "8 builders already have this world open: you will not see the others live in this tab, and your saves still merge.",
    merge: {
      one: "{who} saved changes to something you also changed. Choose whose to keep; the rest of both is kept.",
      other: "{who} saved changes to {count} things you also changed. Choose whose to keep; the rest of both is kept.",
    },
    merged: "Merged {who}'s save",
    mergedAsked: "Merged {who}'s save with your choices",
    goToView: "Go to {name}'s view",
    goToViewOn: "Go to {name}'s view on {place}",
    region: {
      cells: "Tiles {left} to {right} across, {top} to {bottom} down, on {map}",
      marker: "Marker {type}",
      prop: "Prop",
      itemAt: "At {x}, {y} on {layer}, {map}",
      item: "on {layer}, {map}",
      layer: "The layer itself, on {map}",
      map: "The map's settings, layer order or groups",
      world: "The world's settings or terrain names",
    },
  },
  library: {
    framesFailed: "The frames could not be brought: {reason}",
    draftsFull: { one: "The drafts of {pack} do not all fit in this map's art: {count} is drawn from the last compile. Reload to repack it.", other: "The drafts of {pack} do not all fit in this map's art: {count} are drawn from the last compile. Reload to repack it." },
    draftsFailed: "The drafts of {pack} could not be drawn: {reason}",
    terrainsFailed: "Some of the world's other terrains could not be brought, and the rest went on: {reason}",
    tilesetsFailed: "Some of the world's other tilesets could not be brought, and the rest went on: {reason}",
    pages: "{open} of {most} runtime pages, about {mib} MiB of textures and as much of canvases",
    leftOut: "The rest of the terrains and tilesets were left out to keep room for what you pull.",
    restoresCapped: "The graphics lost their memory twice, so this map will draw no more new art. Reload to repack it.",
  },
  terrain: {
    firstStrokeRefused: "That terrain could not be added to this world.",
  },
  pull: {
    pulled: { one: "{who} pulled {what} from {pack}.", other: "{who} pulled {what} from {pack}." },
    pagesFull: "This map draws a lot of art. Reload to repack it.",
    props: { one: "a prop", other: "{count} props" },
    removed: "{who} removed {what} from {pack}.",
    pulledProps: { one: "a pulled prop", other: "{count} pulled props" },
    pulledNamed: "the pulled {name}",
    severalPacks: "several packs",
    anotherPack: "another pack",
  },
  stream: {
    connecting: "Connecting",
    live: "Live",
    sending: "Sending",
    reconnecting: "Reconnecting",
    paused: "Reconnecting, editing paused",
    view: "View only",
    outdated: "A new version of the editor is out; reload",
    expired: "Your login ended; reload and log in again",
    edited: "{who} edited the world",
    edits: { one: "{count} edit", other: "{count} edits" },
    full: {
      world: "This world is full (8 builders): this tab shows its last copy and joins when there is room",
      account: "4 tabs of this account are open: this tab shows its last copy and joins when there is room",
      server: "The build server is full: this tab shows its last copy and joins when there is room",
    },
    stop: {
      denied: "This account may no longer change this world; reload",
      replaced: "The world was replaced outside the editor; reload",
      frozen: "The server stopped taking this world's edits; reload",
    },
    lost: {
      neverSent: { one: "{sentence}. {edits} never sent is lost on reload", other: "{sentence}. {edits} never sent are lost on reload" },
      sent: "{sentence}. {edits} sent may or may not have landed",
      some: "{sentence}. {edits} never sent; {sent} may have landed",
    },
    refusedOpen: "This account may not open this world; ask a lead for the builder role",
    lookOnly: "This account may look at this world but not change it",
    notSent: "{edits} not yet sent",
    notice: {
      pausedNone: "Editing paused: offline for 30 seconds. It starts again when you reconnect",
      paused: {
        one: "Editing paused: offline for 30 seconds. {edits} not yet sent goes out when you reconnect",
        other: "Editing paused: offline for 30 seconds. {edits} not yet sent go out when you reconnect",
      },
      droppedNone: "The connection dropped; reconnecting. You can go on editing for 30 seconds",
      dropped: "The connection dropped; reconnecting. {edits} not yet sent; you can edit for 30 seconds",
      reconnected: "Reconnected: the world is up to date; sending {edits} not yet sent",
      liveAgain: "Live again: the world is up to date",
      liveAgainSent: "Live again: the world is up to date and your edits are with the server",
    },
    refusal: {
      viewOnly: "This tab can only look at the world",
      paused: "Editing is paused until the connection is back",
      notYet: "Not connected yet: editing starts in a moment",
      full: "Too many edits are waiting to be sent; wait a moment",
      travel: "Not changed: that edit is too large to send, or holds a name longer than 1,024 characters",
      limit: "Not changed: a limit would be passed (items, layers, groups, maps, fields or zone types)",
      invalid: "Not changed: the world would not read back with that edit",
      stale: "Not changed: that edit no longer applies to the world as it stands",
    },
    undo: {
      older: "Undid an older edit of yours: what your newer edits changed is gone for now",
      partOlder: "Undid part of an older edit of yours: someone changed the rest since",
      part: "Undid part: someone changed the rest since",
      nothing: "Nothing to undo there: someone changed it since",
      gone: "Nothing to undo there now: what you changed is gone until its deletion is undone",
      empty: "Nothing to undo",
    },
    redo: {
      older: "Redid an older edit of yours: what your newer ones changed is gone for now",
      partOlder: "Redid part of an older edit of yours: someone changed the rest since",
      part: "Redid part: someone changed the rest since",
      nothing: "Nothing to redo there: someone changed it since",
      gone: "Nothing to redo there now: what you changed is gone until its deletion is undone",
      empty: "Nothing to redo",
    },
    lostGone: "That item was removed by {who}; your edit to it was not kept",
    lostFirst: "Your edit was not kept: {who} changed that first",
    history: "{count} of your edits can no longer be undone: a tileset moved in the world's list",
    held: {
      one: "{edits} is not yet with the server; try again when the top bar reads Live",
      other: "{edits} are not yet with the server; try again when the top bar reads Live",
    },
    versionLine: "{who} saved a version",
    versionLineNote: "{who} saved a version: {note}",
    diverged: "live.diverged at sequence {seq}: this tab's copy of the world was not the server's; the server's is read again, and edits not yet answered are kept",
    checksEnded: {
      persistent: "live.diverged again and again: this tab no longer checks its copy against the server's; its unsaved changes are kept in this tab, so save a copy before reloading",
      unbuilt: "the divergence check is off: this page was built without its worker",
      stopped: "the divergence check stopped: {reason}",
    },
  },
  version: {
    saved: "Version saved",
    nothing: "Nothing to save",
    failed: "Version not saved",
    notConnected: "Version not saved: this tab is not connected; try again when it reads Live",
    refused: "Version not saved: the server could not write it, or Save was pressed too fast; try again",
    silent: "Version not saved: the server did not answer; try again",
    note: "Version not saved:",
  },
  check: {
    diverged: "This tab's copy of the world differed from the server's; the server's was read again",
    persistent: "This tab keeps differing from the server. Its unsaved changes are kept here: save a copy first",
  },
  signIn: {
    losing: {
      world: "its unsaved changes to the world",
      footprints: "its unsaved footprints",
      both: "its unsaved changes to the world and its unsaved footprints",
      changes: "its unsaved changes",
    },
    kept: {
      footprints: "your unsaved footprints are kept and come back after it",
      nothing: "nothing in this tab would be lost",
    },
    ended: {
      again: "Your sign-in ended. Sign in again to carry on; nothing is reloaded and nothing is lost.",
      againLosing: "Your sign-in ended. Sign in again to carry on; nothing is reloaded and nothing is lost. Do not reload the page: this tab would lose {losing}.",
      reload: "Your sign-in ended. Reload the page to sign in again; {kept}.",
      download: "Your sign-in ended. Do not reload yet: this tab would lose {losing}. Download your version first.",
      downloadNoFootprints: "Your sign-in ended. Do not reload yet: this tab would lose {losing}. Download your version first. The download does not hold the footprints.",
      retry: "Your sign-in ended. Do not reload the page: this tab would lose {losing}. Allow pop-up windows for this page, then sign in again.",
      none: "Your sign-in ended. Do not reload the page: this tab would lose {losing}.",
    },
    again: "Sign in again",
    reload: "Reload",
    download: "Download my version",
    doorEndedTitle: "Your sign-in ended",
    doorUnreachedTitle: "The server could not be reached",
    back: "You are signed in again. Nothing was lost.",
    blocked: "The sign-in window could not be opened: the browser blocked it.",
    unanswered: "The sign-in window did not finish in time.",
    failedRequest: "A request to the server failed before it was answered: {reason}",
    downloaded: "Your version was downloaded as {name}.",
    downloadFailed: "Your version could not be downloaded: {reason}",
    restored: {
      one: "Your unsaved footprint was brought back after the reload: {names}. Check it, then Save.",
      other: "Your unsaved footprints were brought back after the reload: {names}. Check them, then Save.",
    },
    newer: {
      one: "A footprint you had not saved was saved by someone else meanwhile, so yours was not brought back: {names}.",
      other: "Footprints you had not saved were saved by someone else meanwhile, so yours were not brought back: {names}.",
    },
    gone: {
      one: "An unsaved footprint could not be brought back, because the atlas no longer has the frame: {names}.",
      other: "Unsaved footprints could not be brought back, because the atlas no longer has the frame: {names}.",
    },
    unchecked: {
      one: "An unsaved footprint is still kept in this tab but was not brought back, because the pack's footprints could not be read: {names}. Reload to try again.",
      other: "Unsaved footprints are still kept in this tab but were not brought back, because the pack's footprints could not be read: {names}. Reload to try again.",
    },
    old: {
      one: "An unsaved footprint from {when} is kept in this tab.",
      other: "{count} unsaved footprints from {when} are kept in this tab.",
    },
    bringBack: "Bring them back",
    storeFailed: "Unsaved footprints could not be kept across a reload in this browser. Save before you reload.",
    notKept: "Too many unsaved footprints to keep across a reload. Save before you reload, or these are lost: {names}.",
    listMore: "{names} and {rest} more",
  },
  signedIn: {
    title: "Signed in",
    body: "You are signed in again. You can close this window.",
  },
  diverged: {
    title: "This tab fell out of step with the saved world",
    message: "This tab fell out of step with the saved world, so it cannot go on editing it. The changes made here are kept. Save and reload brings the latest world, and the changes from this tab are then offered to you as a copy or a download.",
    offerTitle: "Your changes from before the reload",
    offer: "This tab fell out of step with the saved world on {when}, and the changes made in it were kept. They cannot be put into a world that is edited live yet. They are safe on the server. Save them as a copy world, or download them.",
    waiting: "Your changes from before the reload are kept on the server. They cannot be put into a world that is edited live yet.",
    copied: "Your changes from before the reload were saved as the world {id}. This world is untouched.",
  },
  update: {
    title: "The editor was updated",
    unsaved: "The editor was updated. Save and reload to update your editor. Your changes are kept for you and are waiting when the new editor opens.",
    saved: "The editor was updated. Reload to update your editor. Nothing in this tab is unsaved.",
    unopened: "The editor was updated, and this page cannot open the world as it is now saved. The new editor can take a minute to arrive. Reload to update your editor.",
    refused: "The editor was updated, and this page can no longer save into the world. Save and reload to update your editor. Your changes are kept for you and are waiting when the new editor opens.",
    carry: "Save and reload",
    carrying: "Saving your changes",
    copyLabel: "Save the copy as (world id)",
    saveCopy: "Save my changes as a copy",
    saving: "Saving the copy",
    download: "Download my version",
    downloading: "Preparing the file",
    reload: "Reload",
    reloadUnsaved: "Reload and lose my changes",
    later: "Not now",
    carryFailed: "Your changes could not be kept for the reload: {reason} Nothing was reloaded and your changes are still in this tab. Download your version, or save it as a copy.",
    pending: "Another tab of yours already kept its changes for this update, and keeping this tab's too would replace them. Nothing was reloaded and this tab's changes are still here. Save them as a copy, or download your version.",
    pendingAt: "Another tab of yours already kept its changes for this update at {time}, and keeping this tab's too would replace them. Nothing was reloaded and this tab's changes are still here. Save them as a copy, or download your version.",
    pendingOwn: "Changes of yours kept for an earlier update were never brought in, and keeping this tab's too would replace them. Nothing was reloaded and this tab's changes are still here. Save them as a copy, or download your version.",
    pendingOwnAt: "Changes of yours kept for an earlier update at {time} were never brought in, and keeping this tab's too would replace them. Nothing was reloaded and this tab's changes are still here. Save them as a copy, or download your version.",
    footprintsFailed: "A footprint was not written, so nothing was reloaded: {reason} Your changes are still in this tab.",
    copied: "Saved as the world {id}. Your changes are safe there; reload when you are ready.",
    downloaded: "Downloaded {name}. It holds this tab's version.",
    downloadFailed: "The file could not be made: {reason}",
  },
  carry: {
    waitTitle: "Your changes are saved for the update",
    wait: "Your changes were saved for the update at {time}. The new editor has not arrived yet, which can take a minute. Reload to try again; your changes are brought in once it opens.",
    reload: "Reload",
    askTitle: "Bring in your earlier changes?",
    ask: "This world holds changes of yours that were saved for an editor update on {when} and never brought in. They can be merged onto the world as it is now. Nothing is written until you save.",
    bringIn: "Bring them in",
    replayed: "Your unsaved changes from before the update were brought in. Check them, then Save.",
    replaced: "An earlier set of changes, kept at {time} by another tab or an earlier try, was replaced by this one. It is still kept on the server: ask for it with Help > Feedback.",
    another: "Another tab of yours kept unsaved changes for the update at {time}. They are waiting to be brought in.",
    discard: "Discard those changes",
    discardTitle: "Discard the changes from before the update?",
    discardBody: "The changes brought in from before the update are dropped, and so is anything else unsaved in this tab. The world opens as it was last saved. Keeping them changes nothing: they stay in this tab until you save.",
    keep: "Keep them",
    discardFailed: "The changes were not discarded: {reason}",
    waiting: "Your unsaved changes from before the update are still waiting. Nothing was brought in.",
    asked: "Changes of yours from {when} are waiting to be brought in.",
    newer: "Your unsaved changes from before the update were written by a newer editor than this one, so this page cannot read them. They are kept; they will be brought in when the newer editor is back.",
    unreadable: "Your unsaved changes from before the update could not be read by this editor ({reason}). They are kept on the server. Download them and report it with Help > Feedback.",
    download: "Download them",
    copied: "The version your changes were based on could not be read, so they could not be merged. They were saved as the world {id} instead; this world is untouched.",
    open: "Open it",
    copyFailed: "Your unsaved changes from before the update could not be merged, and saving them as a copy failed ({reason}). They are kept on the server. Download them and report it with Help > Feedback.",
    unreachable: "Your unsaved changes from before the update could not be fetched ({reason}). They are kept on the server.",
    retry: "Try again",
    streamTitle: "Your changes from before the update",
    stream: "Changes of yours were kept for an editor update on {when}. This world is now edited live, and kept changes cannot be merged into a live world yet. They are safe on the server. Save them as a copy world, or download them.",
    streamCopy: "Save them as a copy",
    streamWaiting: "Your changes from before the update are kept on the server. They cannot be merged into a live world yet.",
    streamShow: "Show the choices",
    streamCopied: "Your changes from before the update were saved as the world {id}. This world is untouched.",
    notNow: "Not now",
    elsewhere: {
      one: "You have unsaved changes from before an update in the world {worlds}. Open it to bring them in.",
      other: "You have unsaved changes from before an update in the worlds {worlds}. Open each to bring them in.",
    },
    settleFailed: "The save landed, but the kept changes could not be marked as brought in ({reason}); the next save tries again.",
  },
  behind: {
    retry: "Try again",
    failed: "{who}'s save is not in this tab: {reason}",
    declined: "{who}'s save is not in this tab: the choice between your changes and theirs was left for later.",
    offline: "Not connected",
    offlineTitle: "Not connected: other builders' saves will not reach this tab until it reconnects.",
    refused: "Live updates are off in this tab: this account may not join the world's live channel. Your saves still merge.",
    cause: {
      outdated: "the saved version was written by a newer editor than this page, so this page cannot read it. The editor was updated",
      unreachable: "the server could not be reached ({reason})",
      unreadable: "this editor could not understand the saved version or the merged result ({reason})",
    },
  },
  overview: {
    title: "{name}, {width} by {height} tiles",
    close: "Close overview",
    hint: "Look tool: click to go to that tile. Wheel zooms. O or Escape closes.",
    drawing: "Drawing the map: {done} of {total}",
    updating: "Updating {waiting} of {total}",
    middle: "Showing the middle of a large map",
    noCanvas: "The overview could not open: this browser would not make a picture.",
    failed: "Part of the overview could not be drawn ({reason}).",
    inert: "Close the overview to edit the map",
    goToCentre: "Go to the tile at the middle of the overview",
    neighbourPress: "That is {name}. Use the Look tool to open it.",
    unavailable: {
      noLayer: "Choose a layer to paint on.",
      brushSet: "Pick a brush set to paint props here. One prop is a few pixels in the overview.",
      select: "Select works at normal zoom: a prop is 4 to 6 pixels here.",
      notDrawn: "Footprints and zones are not drawn in the overview.",
      markers: "Close the overview to place markers.",
      doors: "Doors are not drawn in the overview.",
      fillLayer: "Fill paints terrain and tile layers.",
      editOnMap: "Close the overview to edit on the map.",
      measure: "Close the overview to measure here.",
    },
    tag: {
      brush: { one: "{name}, {count} tile", other: "{name}, {count} tiles" },
      eraseBrush: { one: "Erase {name}, {count} tile", other: "Erase {name}, {count} tiles" },
      terrain: { one: "Terrain: {name}, {count} tile", other: "Terrain: {name}, {count} tiles" },
      eraseTerrain: { one: "Erase terrain, {count} tile", other: "Erase terrain, {count} tiles" },
      tile: { one: "Tile: {name}, {count} tile", other: "Tile: {name}, {count} tiles" },
      eraseTile: { one: "Erase tiles, {count} tile", other: "Erase tiles, {count} tiles" },
      fill: "Fill: {name}",
      clear: "Clear fill",
    },
  },
  keys: {
    mirrorX: "Mirror horizontally",
    mirrorY: "Mirror vertically",
    sortDown: "Sort offset down",
    sortUp: "Sort offset up",
    sortDownPixel: "Sort offset down 1 px",
    sortUpPixel: "Sort offset up 1 px",
    standInKept: "The player stand-in is view state and is never deleted",
    nothingToDelete: "Nothing to delete; select a prop, a marker or tiles first",
    propsWait: { one: "Switch to Props to delete the selected prop", other: "Switch to Props to delete the {count} selected props" },
    markersWait: { one: "Switch to Markers to delete the selected marker", other: "Switch to Markers to delete the {count} selected markers" },
    pasteTiles: "Paste tiles",
    nothingToPaste: "Nothing to paste; copy or cut tiles first",
    moveArea: "Move the area",
    pasteArea: "Paste area",
    pasteCopy: "Paste the copy",
    paintRectangle: "Paint the rectangle",
    nothingToConfirm: "Nothing to confirm: no rectangle waits and no area's move is pending",
    clearSelection: "Clear selection",
    nothingInHand: "Nothing is selected or in hand",
    sortIsProps: "The sort offset is a prop's",
    selectTiles: "Select tiles or take a tile in hand first",
    selectOneProp: "Select one prop to copy it to hand",
    noProp: "No prop is selected",
    copyProp: "Copy prop to hand, with its behaviour",
    selectPropOrFrame: "Select a prop or take a frame in hand first",
    nudge: "Nudge the selected prop {direction} 1 px",
    nudgeStandIn: "Nudge the selected prop or stand-in {direction} 1 px",
    selectPropOrStandIn: "Select a prop or the stand-in first",
    stepArea: "Step the pending area {direction} one tile",
    moveProp: "Move the selected prop {direction} one collision cell",
    nudgeMarker: "Move the selected marker {direction} one tile",
    moveMarker: "Move the selected marker {direction} one cell",
    selectPropOrMarker: "Select a prop or a marker first",
    frameSelection: "Frame selection",
    nothingSelected: "Nothing is selected",
    pan: "Pan {direction}",
    panStandIn: "Pan {direction}, or walk the selected stand-in",
    panHeld: "Pan with the left button while held",
    spaceFocus: "Space presses the control that has focus; click the map first",
    directions: { left: "left", right: "right", up: "up", down: "down" },
  },
  brush: {
    why: {
      none: "Nothing placed: {reason}",
      few: "Placed {placed} of {target}: {reason}",
      gap: {
        one: "{count} of {tried} tries was too close to this brush's own props",
        other: "{count} of {tried} tries were too close to this brush's own props",
      },
      props: {
        one: "{count} of {tried} tries had other props in the way",
        other: "{count} of {tried} tries had other props in the way",
      },
      ground: {
        one: "{count} of {tried} tries was on ground this brush may not use",
        other: "{count} of {tried} tries were on ground this brush may not use",
      },
      edge: {
        one: "{count} of {tried} tries ran off the edge of the map",
        other: "{count} of {tried} tries ran off the edge of the map",
      },
      full: "This ground is already at the chosen density",
    },
    stroke: {
      cleared: {
        one: "placed {count} prop, cleared {cleared} ground cover",
        other: "placed {count} props, cleared {cleared} ground cover",
      },
      stoppedCleared: "Stopped at {count} cleared",
      alsoCleared: "Cleared {count} ground cover",
    },
    groundPick: {
      nothing: "Nothing painted there",
    },
    deletedBy: "{name} was deleted by {who}; it left your hand",
  },
  neighbours: {
    noMap: "no map",
    draft: "draft",
    blank: "blank",
  },
  doorSettings: {
    entry: "Entry",
    walk: "Walk in",
    press: "Press E",
    leadsTo: "Leads to {to}",
    combat: "In combat",
    refuse: "Refuse",
    allow: "Let through",
  },
  gate: {
    closesTitle: "Closes by itself",
    closesHint: "The gate closes when any condition turned on here is met.",
    presetBehind: "Door behind me",
    presetTimed: "Timed",
    unlock: "Unlock",
    closeAfter: "Close after (seconds)",
    closeBehind: "Close behind the opener",
    closeOnLeave: "Close when the opener leaves",
    closeWhenEmpty: "Close when nobody is within (tiles)",
    closeDelay: "Wait before closing (seconds)",
    unlockVillage: "Everyone, for good",
    unlockEach: "A key each time",
    unlockCharacter: "That character",
    unlockCharacterNote: "Only that character is let through for good. An open gate is still open to everyone.",
    errorCloseAfter: "Use 0, or a whole number from 2 to 3600.",
    errorCloseWhenEmpty: "Use 0, or a whole number from 1 to 32.",
    errorCloseDelay: "Use a whole number from 0 to 30.",
    errorFlag: "Turn it on or off.",
    errorUnlock: "Choose one of the listed ways.",
  },
  spawn: {
    levelMin: "Lowest level",
    levelMax: "Highest level",
    levelRange: "Use a whole number from 1 to {cap}.",
    levelOrder: "Lowest must not be above highest.",
  },
} as const;
